import { useEffect, useState } from 'react';

import { Flex, ProgressCircle, Text } from '@spotify-internal/encore-web';

import styles from './OverrideHandler.module.css';

const SESSION_STORAGE_OVERRIDE_TOKEN = 'confidence-override-token';
const LOCAL_OVERRIDE_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

const isLikelyJwt = (token: unknown): token is string =>
  typeof token === 'string' &&
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);

const getReferrerOrigin = (referrer: string): string | undefined => {
  if (!referrer) {
    return undefined;
  }

  try {
    return new URL(referrer).origin;
  } catch {
    return undefined;
  }
};

export const isAllowedOverrideOpenerOrigin = (origin: string): boolean => {
  try {
    const { hostname, protocol } = new URL(origin);
    const isSpotifyDomain =
      hostname.endsWith('.spotify.net') || hostname.endsWith('.spotify.com');
    const isBackofficeHost =
      hostname === 'epx-backoffice.services.guc3.spotify.net' ||
      hostname.includes('backoffice');

    if (!['http:', 'https:'].includes(protocol)) {
      return false;
    }

    return (
      LOCAL_OVERRIDE_HOSTS.has(hostname) ||
      (isSpotifyDomain && isBackofficeHost)
    );
  } catch {
    return false;
  }
};

const getAllowedOrigin = (
  origin: string | null | undefined,
): string | undefined =>
  origin && isAllowedOverrideOpenerOrigin(origin) ? origin : undefined;

export const getAllowedOverrideOpenerOrigin = (
  search: string,
  referrer: string,
): string | undefined => {
  const openerOrigin = new URLSearchParams(search).get('openerOrigin');

  return (
    getAllowedOrigin(openerOrigin) ??
    getAllowedOrigin(getReferrerOrigin(referrer))
  );
};

const redirectToHome = () => window.location.replace('/');

interface OverrideHandlerProps {
  onComplete?: () => void;
}

const OverrideHandler = ({
  onComplete = redirectToHome,
}: OverrideHandlerProps) => {
  const [status, setStatus] = useState('Waiting for token...');

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const nonce = searchParams.get('nonce');
    const openerOrigin = getAllowedOverrideOpenerOrigin(
      window.location.search,
      document.referrer,
    );

    if (!nonce || !window.opener || !openerOrigin) {
      setStatus('Invalid override request.');
      return () => {};
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== window.opener) return;
      if (event.origin !== openerOrigin) return;
      if (event.data?.type !== 'override-token') return;
      if (!isLikelyJwt(event.data?.token)) return;
      if (event.data?.nonce !== nonce) return;
      window.sessionStorage.setItem(
        SESSION_STORAGE_OVERRIDE_TOKEN,
        event.data.token,
      );
      setStatus('Token received, redirecting...');
      onComplete();
    };

    window.addEventListener('message', handleMessage);

    window.opener.postMessage({ type: 'override-ready', nonce }, openerOrigin);

    return () => window.removeEventListener('message', handleMessage);
  }, [onComplete]);

  return (
    <Flex
      className={styles.root}
      direction="column"
      alignItems="center"
      justifyContent="center"
      gap="base"
      role="status"
    >
      <ProgressCircle aria-label={status} size="medium" />
      <Text variant="bodySmall" semanticColor="textSubdued">
        {status}
      </Text>
    </Flex>
  );
};

export default OverrideHandler;
